import { API_URL } from '../config';
import { getAuthHeaders } from './auth';

/** Turn API path or S3 URL into something the browser can load. */
export function assetUrl(path) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/api/')) return `${API_URL}${path}`;
  return path;
}

/** Copy a generated asset (S3 URL or API path) to the system clipboard as an image. */
export async function copyImageToClipboard(pathOrUrl) {
  const url = assetUrl(pathOrUrl);
  if (!url) throw new Error('No image to copy');

  if (!navigator.clipboard?.write) {
    throw new Error('Clipboard is not available in this browser');
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not load image');

  let blob = await res.blob();
  const type = blob.type?.startsWith('image/') ? blob.type : 'image/png';
  if (blob.type !== type) {
    blob = new Blob([blob], { type });
  }

  await navigator.clipboard.write([new ClipboardItem({ [type]: blob })]);
}

export function buildAutoPrompt(rootPath, secondaryPaths) {
  void rootPath;
  const tokens = ['@1', ...secondaryPaths.map((_, i) => `@${i + 2}`)];
  return tokens.join(' ');
}

/** @typedef {{ key: string, label: string, count: number, cover?: string, variants: string[] }} SpiceFolder */

export async function fetchSpices() {
  const res = await fetch(`${API_URL}/api/brand-refs`);
  if (!res.ok) {
    const hint =
      res.status === 0 || res.type === 'opaque'
        ? 'Cannot reach API — is the server running? (npm start in retro-main/server)'
        : `Spices ${res.status}`;
    throw new Error(hint);
  }
  const data = await res.json();
  return {
    folders: data.folders || [],
    assetsByFolder: data.assetsByFolder || {},
    root: data.root,
  };
}

/** @deprecated alias */
export const fetchBrands = fetchSpices;

export async function uploadSpiceRef(getAccessTokenSilently, spiceKey, file) {
  const headers = await getAuthHeaders(getAccessTokenSilently);
  if (!headers) throw new Error('Not signed in');
  const form = new FormData();
  form.append('file', file);
  form.append('spiceKey', spiceKey);
  const { Authorization, ...rest } = headers;
  const res = await fetch(`${API_URL}/api/spice/upload`, {
    method: 'POST',
    headers: { Authorization },
    body: form,
  });
  if (!res.ok) {
    let msg = `Upload ${res.status}`;
    try {
      const d = await res.json();
      if (d?.error) msg = d.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
}

export async function createSpiceFolder(getAccessTokenSilently, name) {
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const res = await fetch(`${API_URL}/api/spice/folders`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Create spice ${res.status}`);
  return res.json();
}

export async function fetchLibrary(getAccessTokenSilently) {
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const res = await fetch(`${API_URL}/api/brand-assets/library`, { headers });
  if (!res.ok) throw new Error(`Library ${res.status}`);
  return res.json();
}

export async function postGenerate(getAccessTokenSilently, body) {
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const res = await fetch(`${API_URL}/api/brand-assets/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `Generate ${res.status}`;
    try {
      const data = await res.json();
      if (data?.error) msg = data.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
}

export async function fetchJob(getAccessTokenSilently, jobId) {
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const res = await fetch(`${API_URL}/api/brand-assets/jobs/${encodeURIComponent(jobId)}`, {
    headers,
  });
  if (!res.ok) throw new Error(`Job ${res.status}`);
  const data = await res.json();
  const job = data.job || data;
  return {
    id: job.id,
    status: job.status,
    imagePath: job.imagePath || job.asset?.s3Url,
    error: job.error,
  };
}
